import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Heart, PlusCircle, LogOut, UserCircle2, Menu, X, ShieldCheck, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import { AlertsBell } from "@/components/AlertsBell";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/buy", label: "Buy" },
  { to: "/rent", label: "Rent" },
  { to: "/map", label: "Map" },
  { to: "/neighborhoods", label: "Neighborhoods" },
  { to: "/agents", label: "Agents" },
];

export const Navbar = ({ onAuth }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 glass border-b border-[var(--line)]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'var(--teak)' }}>
            <span className="font-serif text-[var(--gold)] text-lg font-bold">ನ</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif text-lg font-semibold text-[var(--teak)]">NimmaSwathu</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)]">Hyperlocal · Mysuru</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive ? "bg-[var(--surface)] text-[var(--teak)]" : "text-[var(--ink-muted)] hover:text-[var(--teak)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-link-admin" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-[var(--surface)] text-[var(--teak)]" : "text-[var(--primary)]"}`}>
              <ShieldCheck className="w-4 h-4 inline mr-1" /> Verify
            </NavLink>
          )}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <AlertsBell />
              <button
                onClick={() => navigate("/dashboard")}
                className="btn-ghost"
                data-testid="nav-dashboard-btn"
              >
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </button>
              <button
                onClick={() => navigate("/favorites")}
                className="btn-ghost"
                data-testid="nav-favorites-btn"
              >
                <Heart className="w-4 h-4" /> Saved
              </button>
              <button onClick={() => navigate("/post")} className="btn-primary" data-testid="nav-post-btn">
                <PlusCircle className="w-4 h-4" /> Post Property
              </button>
              <div className="flex items-center gap-2 pl-3 border-l border-[var(--line)]">
                <UserCircle2 className="w-5 h-5 text-[var(--ink-muted)]" />
                <span className="text-sm text-[var(--ink)] max-w-[120px] truncate" data-testid="nav-user-name">{user.name}</span>
                <button onClick={logout} className="p-1.5 rounded-full hover:bg-[var(--surface)]" title="Logout" data-testid="nav-logout-btn">
                  <LogOut className="w-4 h-4 text-[var(--ink-muted)]" />
                </button>
              </div>
            </>
          ) : (
            <>
              <button onClick={() => onAuth?.("login")} className="btn-ghost" data-testid="nav-login-btn">Sign In</button>
              <button onClick={() => onAuth?.("register")} className="btn-primary" data-testid="nav-register-btn">Join NimmaSwathu</button>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[var(--line)] bg-white px-4 py-4 space-y-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-lg text-sm ${isActive ? "bg-[var(--surface)]" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-2 border-t border-[var(--line)] flex flex-col gap-2">
            {user ? (
              <>
                <button onClick={() => { navigate("/favorites"); setOpen(false); }} className="btn-ghost justify-start">
                  <Heart className="w-4 h-4" /> Saved
                </button>
                <button onClick={() => { navigate("/post"); setOpen(false); }} className="btn-primary justify-start">
                  <PlusCircle className="w-4 h-4" /> Post Property
                </button>
                <button onClick={() => { logout(); setOpen(false); }} className="btn-ghost justify-start">
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { onAuth?.("login"); setOpen(false); }} className="btn-ghost">Sign In</button>
                <button onClick={() => { onAuth?.("register"); setOpen(false); }} className="btn-primary">Join NimmaSwathu</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
